import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import InActivityLogoutModal from "../components/InActivityLogoutModal/InActivityLogoutModal";
import { getCookie, performLogout } from "./index";

// <----- 30 mins total: warn at 29 mins, then a 60s countdown ----->
const IDLE_LIMIT_MS = 1740000;
const COUNTDOWN_SECONDS = 60;
const POLL_INTERVAL_MS = 5000;
const ACTIVITY_EVENTS = ["click", "keypress", "scroll", "mousemove"];

const readExpiryTime = () => {
  const stored = localStorage.getItem("expireTime");
  const parsed = stored ? parseInt(stored, 10) : 0;
  return Number.isNaN(parsed) ? 0 : parsed;
};

const InactivityLogoutTimer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [showAlert, setShowAlert] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);

  // The inactivity timer belongs to an authenticated session and nothing else.
  // Gating on the auth cookie — instead of a hard-coded list of public paths —
  // means the "Session Timeout" modal can never reach a logged-out visitor,
  // including on auth routes (/skypoint-login, /skypoint-2fa, /reset-password,
  // ...) that such a list would inevitably fall behind on. The token cookie is
  // written only after full authentication, so it is a safe proxy for "signed
  // in": every 2FA service clears the session before verifying the code.
  const [hasSession, setHasSession] = useState(() =>
    Boolean(getCookie("token"))
  );

  // Login and logout write the cookie outside of React, so there is no state
  // change to subscribe to — re-read it on navigation and on a short poll.
  useEffect(() => {
    const syncSession = () => setHasSession(Boolean(getCookie("token")));
    syncSession();
    const intervalId = setInterval(syncSession, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [location.pathname]);

  // No session: no countdown, no modal, and drop any expiry a previous session
  // left in localStorage so a stale timestamp can't trip the warning on the
  // login page or immediately after the next sign-in.
  useEffect(() => {
    if (!hasSession) {
      setShowAlert(false);
      setCountdown(COUNTDOWN_SECONDS);
      localStorage.removeItem("expireTime");
    }
  }, [hasSession]);

  useEffect(() => {
    if (!hasSession) return undefined;

    const updateExpiryTime = () => {
      localStorage.setItem("expireTime", (Date.now() + IDLE_LIMIT_MS).toString());
    };

    const checkForInactivity = () => {
      if (readExpiryTime() < Date.now()) {
        setShowAlert(true);
      }
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, updateExpiryTime)
    );

    // Re-seeding on every showAlert flip is what gives "Stay Logged In" a fresh
    // 29 minutes without the dismissal itself having to touch localStorage.
    updateExpiryTime();
    const intervalId = setInterval(checkForInactivity, POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, updateExpiryTime)
      );
    };
  }, [hasSession, showAlert]);

  useEffect(() => {
    if (!hasSession || !showAlert) return undefined;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          performLogout("/", "inactivity_logout");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [hasSession, showAlert]);

  // Keep the Mantine modal's open state in lockstep with the warning itself, so
  // it can never be visible while no countdown is running (or hidden while one
  // is, which would end in a logout the user never saw coming).
  useEffect(() => {
    if (hasSession && showAlert) {
      open();
    } else {
      close();
    }
  }, [hasSession, showAlert, open, close]);

  return (
    hasSession &&
    showAlert && (
      <InActivityLogoutModal
        opened={opened}
        close={close}
        setShowAlert={setShowAlert}
        countdown={countdown}
        setCountdown={setCountdown}
        navigate={navigate}
      />
    )
  );
};

export default InactivityLogoutTimer;
